/**
 * @file NavLink.tsx
 * @description Sidebar nav link с активным состоянием (phase08.5 shadcn).
 *
 * Использует react-router NavLink — добавляет класс `active` при совпадении
 * маршрута. Стилизуем под Chaiberi Editorial: на hover — `bg-sidebar-accent`,
 * на active — `bg-sidebar-accent + text-sidebar-primary-foreground` через
 * primary-gradient (для emphasis).
 *
 * phase19.31: счётчик — 12px по шкале (без `text-[10px]`); `forceActive` —
 * подсветка пункта, когда страница относится к нему не по адресу (разделы
 * страницы «Настройки» под своими адресами); длинная подпись переносится
 * на вторую строку, а не обрезается многоточием (замечание владельца);
 * `inset` — вложенный пункт группы: значок чуть меньше значка группы
 * (18 против 20px) и стоит правее, чтобы вложенность читалась.
 */

import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import { cn } from "@/lib/utils";

interface SidebarNavLinkProps {
  to: string;
  icon?: ReactNode;
  label: string;
  end?: boolean;
  /**
   * Пути-исключения: если current pathname начинается с одного из них,
   * ссылка НЕ считается активной, даже если её prefix-матч сработал бы.
   * Нужно для родительских ссылок, у которых есть «вынесенные» дочерние
   * разделы с собственной sidebar-ссылкой (например, `/avito` vs `/avito/finance`).
   */
  excludePaths?: string[];
  /** Счётчик справа от подписи (например, отзывы на модерации); 0 — не рисуем. */
  badge?: number;
  /** Подсветить пункт независимо от адреса. */
  forceActive?: boolean;
  /** Вложенный пункт группы: отступ слева и значок меньше. */
  inset?: boolean;
}

export function SidebarNavLink({
  to,
  icon,
  label,
  end,
  excludePaths,
  badge,
  forceActive = false,
  inset = false,
}: SidebarNavLinkProps) {
  const { pathname } = useLocation();
  const excludedActive = excludePaths?.some((p) => pathname.startsWith(p)) ?? false;
  return (
    <NavLink
      data-slot="sidebar-link"
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-md py-2 pr-3 text-sm font-medium transition-colors",
          inset ? "pl-9 [&_svg]:size-4.5" : "pl-3 [&_svg]:size-5",
          "[&_svg]:shrink-0",
          (isActive && !excludedActive) || forceActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-hover hover:text-sidebar-hover-foreground",
        )
      }
    >
      {icon}
      <span className="min-w-0 flex-1 break-words">{label}</span>
      {badge ? (
        <span className="min-w-6 shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-center text-xs font-semibold leading-tight text-on-primary tabular-nums">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}
